// src/WebSocketContext.js
import { createContext, useCallback, useContext, useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';
import useWebSocket, { ReadyState } from 'react-use-websocket';

import clientUtils from '@/utils/clientUtils';

import { WebSocketActionType } from '../enums';
import { LocalStorageService } from '../services/local-storage';
import { applicationDataSelector } from '../store/slices/application-data';
import { useGlobalData } from './GlobalDataProvider';

const WebSocketContext = createContext(null);

type Props = {
  children: React.ReactNode;
};

export const WebSocketProvider = ({ children }: Props) => {
  const { globalData } = useGlobalData();
  const { ouData } = useSelector(applicationDataSelector);
  const socketUrl = useMemo(
    () => globalData?.notificationServiceWebSocketUrl || '',
    [globalData?.notificationServiceWebSocketUrl]
  );

  const { sendJsonMessage, lastJsonMessage, readyState, getWebSocket } = useWebSocket(
    socketUrl,
    { onClose: () => console.error('WebSocket connection closed.') },
    socketUrl !== ''
  );

  const closeWebSocket = useCallback(() => {
    const socket = getWebSocket();
    if (socket && readyState === ReadyState.OPEN) {
      socket.close();
    }
  }, [readyState, getWebSocket]);

  useEffect(() => {
    try {
      if (clientUtils.isNullOrWhitespace(ouData?.ouId)) {
        console.error('Could not establish websocket due to empty ouId.', { ouData });
        throw new Error('Could not establish websocket due to empty ouId.');
      }

      if (readyState === ReadyState.OPEN) {
        const token = LocalStorageService.getItem('ACCESS_TOKEN');
        const connectToTopicMsg = {
          action: WebSocketActionType.connectToTopic,
          topicId: ouData?.ouId,
          accessToken: token,
        };
        sendJsonMessage(connectToTopicMsg);
      }
    } catch (error) {
      console.error('Error connecting to websocket', error);
    }

    const interval = setInterval(() => {
      try {
        if (readyState === ReadyState.OPEN) {
          sendJsonMessage({
            action: WebSocketActionType.keepAlive,
          });
        }
      } catch (error) {
        console.error('Error with keepAlive websocket', error);
      }
    }, 300000); // 300000 milliseconds = 5 minute

    return () => {
      // window.removeEventListener('beforeunload', handleUnload);
      try {
        clearInterval(interval);
        const socket = getWebSocket();
        if (socket && readyState === ReadyState.OPEN) {
          socket.close();
        }
      } catch (error) {
        console.error('Error cleaning up websocket', error);
      }
    };
  }, [readyState, ouData]);

  const value = useMemo(() => {
    return {
      lastJsonMessage,
      closeWebSocket,
    };
  }, [lastJsonMessage, closeWebSocket]);

  return <WebSocketContext.Provider value={value}>{children}</WebSocketContext.Provider>;
};

export const useWebSocketContext = () => {
  return useContext(WebSocketContext);
};
